/**
 * KeywordByteCounter - Amazon Backend Keyword Validator
 *
 * Frontend-only utility for validating Amazon.de backend keywords (250 byte limit).
 * Shows real-time byte count and character count - critical because umlauts (ä, ö, ü)
 * consume 2 bytes but count as 1 character.
 *
 * @accessibility WCAG 2.1 AA + BFSG compliant
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calculator, Copy, Trash2, FilterX } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, Badge, Button, Textarea } from '@/components/v2/ui';
import { countUtf8Bytes, removeDuplicateKeywords } from '@/lib/utils';
import { ByteStatistics } from './ByteStatistics';
import { InfoAlerts } from './InfoAlerts';

export function KeywordByteCounter() {
  const { t } = useTranslation();
  const [keywords, setKeywords] = useState('');

  const byteCount = countUtf8Bytes(keywords);
  const charCount = keywords.length;

  const getBadgeVariant = (): 'success' | 'warning' | 'error' => {
    if (byteCount > 250) return 'error';
    if (byteCount >= 200) return 'warning';
    return 'success';
  };

  const handleCopy = () => {
    if (!keywords) return toast.error(t('toast.error.noKeywordsToCopy'));
    navigator.clipboard.writeText(keywords);
    toast.success(t('toast.success.keywordsCopied'));
  };

  const handleRemoveDuplicates = () => {
    if (!keywords) return toast.error(t('toast.error.noKeywords'));
    setKeywords(removeDuplicateKeywords(keywords));
    toast.success(t('toast.success.duplicatesRemoved'));
  };

  const handleClear = () => {
    setKeywords('');
    toast.success(t('editor.keywordsDeleted'));
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 shadow-lg shadow-orange-500/30">
          <Calculator className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white tracking-tight">
            {t('keywordByteCounter.title')}
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            {t('keywordByteCounter.subtitle')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input Section (2/3 width) */}
        <Card className="lg:col-span-2 rounded-xl shadow-sm border-neutral-200 dark:border-neutral-800">
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <label htmlFor="keywords-input" className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                {t('keywordByteCounter.inputLabel')}
              </label>
              <Textarea
                id="keywords-input"
                rows={8}
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder={t('keywordByteCounter.placeholder')}
                className="font-mono text-sm resize-none"
              />
            </div>

            {/* Byte Count Badge + Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant={getBadgeVariant()} className="text-sm px-3 py-1">
                  {t('keywordByteCounter.byteBadge', { count: byteCount })}
                </Badge>
                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                  {t('keywordByteCounter.charCount', { count: charCount })}
                </span>
              </div>

              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleCopy} disabled={!keywords}>
                  <Copy className="h-3 w-3 mr-1" />
                  {t('common.copy')}
                </Button>
                <Button size="sm" variant="outline" onClick={handleRemoveDuplicates} disabled={!keywords}>
                  <FilterX className="h-3 w-3 mr-1" />
                  {t('keywordByteCounter.duplicates')}
                </Button>
                <Button size="sm" variant="outline" onClick={handleClear} disabled={!keywords}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Section (1/3 width) */}
        <div className="space-y-4">
          <ByteStatistics byteCount={byteCount} charCount={charCount} />
          <InfoAlerts byteCount={byteCount} />
        </div>
      </div>
    </div>
  );
}

export default KeywordByteCounter;
